import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import * as XLSX from 'xlsx';
import {
    Trash2,
    Download,

    Plus,
    ChevronLeft,
    ChevronRight,
    Pencil,
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import MapViewer from "Common/Components/MapViewer";
import { getRoleId } from "Common/Components/getRoleId";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== تایپ‌ها ==========
interface OrderImage {
    created_at: string;
    created_at_jalali: string;
    id: number;
    category: string;
    url: string;
    file_name: string;
    file_size: number;
}

interface Order {
    id: number;
    order_id: string;
    order_code: string;
    distribution_period: string;
    distribution_period_code: string;
    distribution_stage: string;
    distribution_stage_code: string;
    order_date: string;
    order_date_jalali: string;
    voucher_date: string;
    voucher_date_jalali: string;
    flour_factory: string;
    flour_factory_code: string;
    factory_delivery_date: string;
    factory_delivery_date_jalali: string;
    shipping_company: string;
    shipping_contractor: string;
    driver_name: string;
    driver_national_id: string;
    car_plate: string;
    car_type: string;
    province: string;
    province_id: number;
    city: string;
    city_id: number;
    baker_name: string;
    baker_code: string;
    baker_national_id: string;
    baker_phone: string;
    /** آدرس نانوا، خوانده‌شده از پروفایل خبازی توسط Backend */
    baker_address?: string | null;
    voucher_bag_count: number;
    total_bag_count: number;
    delivery_date: string;
    delivery_date_jalali: string;
    driver_location_lat: number | null;
    driver_location_lng: number | null;
    auto_counted_count: number | null;
    discharge_date: string | null;
    discharge_date_jalali: string | null;
    driver_discharge_count: number | null;
    inspector_counted_count: number | null;
    inspection_date: string | null;
    inspection_date_jalali: string | null;
    inspector_location_lat: number | null;
    inspector_location_lng: number | null;
    images: OrderImage[];
}

interface ApiResponse {
    total: number;
    size: number;
    pages: number;
    items: Order[];
}

// ========== توابع تبدیل تاریخ ==========
const shamsiToGregorianISO = (shamsiDate: DateObject | null): string => {
    if (!shamsiDate) return "";
    try {
        const gregorian = shamsiDate.toDate();
        return gregorian.toISOString();
    } catch (e) {
        return "";
    }
};

const gregorianISOToShamsi = (isoString: string | null): DateObject | null => {
    if (!isoString) return null;
    try {
        const date = new Date(isoString);
        return new DateObject({ date, calendar: persian }).setLocale(persian_fa);
    } catch (e) {
        return null;
    }
};

const ManageOrders = () => {
    // ========== state اصلی ==========
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const navigator = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("orders", (filter) => {
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        setSearchTerm(filter.search || "");
        setCurrentPage(1);
    });

    const [showOrderModal, setShowOrderModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImages, setSelectedImages] = useState<OrderImage[]>([]);

    const [showFormModal, setShowFormModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editOrderId, setEditOrderId] = useState<number | null>(null);
    // تاریخ‌های فرم (شمسی)
    const [orderDate, setOrderDate] = useState<DateObject | null>(new DateObject({ calendar: persian }).setLocale(persian_fa));
    const [voucherDate, setVoucherDate] = useState<DateObject | null>(null);
    const [factoryDeliveryDate, setFactoryDeliveryDate] = useState<DateObject | null>(null);
    const [deliveryDate, setDeliveryDate] = useState<DateObject | null>(null);

    // لیست‌های استان و شهر
    const [provincesList, setProvincesList] = useState<{ province_id: number; province: string }[]>([]);
    const [citiesList, setCitiesList] = useState<{ city_id: number; city: string }[]>([]);

    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const userRole = getRoleId(localStorage.getItem("app_havir")) || 50;

    // ========== تابع دریافت داده‌ها با وابستگی به searchTerm ==========
    const fetchOrders = useCallback(async (page: number, size: number) => {
        setLoading(true);
        setErrorMessage("");
        try {
            const params: Record<string, any> = {
                page,
                size,
                sort_by: "id",
                sort_order: "desc",
            };
            const normalizedSearch = searchTerm?.trim().replace(/\s+/g, " ");
            if (normalizedSearch) {
                params.search = normalizedSearch;
            }
            if (sortBy) {
                params.sort_by = sortBy;
                params.sort_order = sortOrder;
            }
            console.log("Search params:", params);
            const res = await axios.get<ApiResponse>(
                "https://rasad-api.havirard.ir/monitoring/orders",
                { params, headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
            );
            setOrders(res.data.items ?? []);
            setTotal(res.data.total ?? 0);
            setTotalPages(res.data.pages ?? 0);
        } catch (err: any) {
            console.error("Fetch orders error:", err);
            if (err.status === 401) return navigator("/login");
            setErrorMessage("خطا در دریافت اطلاعات");
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, sortBy, sortOrder]);

    // ========== فقط یک useEffect برای بارگذاری داده‌ها ==========
    useEffect(() => {
        fetchOrders(currentPage, selectedNumber);
    }, [currentPage, selectedNumber, fetchOrders]);

    // ========== دریافت لیست استان‌ها و شهرها ==========
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [provRes, cityRes] = await Promise.all([
                    axios.get("https://rasad-api.havirard.ir/dropdown/provinces", {
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }),
                    axios.get("https://rasad-api.havirard.ir/dropdown/cities", {
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }),
                ]);
                setProvincesList(provRes.data || []);
                setCitiesList(cityRes.data || []);
            } catch (err) {
                console.error("Dropdown fetch error:", err);
            }
        };
        fetchDropdowns();
    }, []);

    // ========== هندلرها ==========
    const handleSelect = (value: number) => {
        setSelectedNumber(value);
        const totalItems = total || 0;
        const newTotalPages = Math.ceil(totalItems / value);
        if (currentPage > newTotalPages) {
            setCurrentPage(Math.max(1, newTotalPages));
        }
        // نیازی به فراخوانی دستی نیست، useEffect اجرا می‌شود
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        // useEffect اجرا می‌شود
    };

    const handleSearchChange = (val: string) => {
        setSearchTerm(val);
        setCurrentPage(1);
        // useEffect با تغییر searchTerm و currentPage اجرا می‌شود
    };

    // حذف
    const handleDeleteClick = (id: number) => {
        setDeleteId(id);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (deleteId === null) return;
        try {
            await axios.delete(
                `https://rasad-api.havirard.ir/monitoring/orders/${deleteId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                    },
                }
            );
            toast.success("سفارش با موفقیت حذف شد");
            setDeleteModal(false);
            setDeleteId(null);
            // بعد از حذف، دوباره بارگذاری کن
            fetchOrders(currentPage, selectedNumber);
        } catch (err) {
            console.error("Delete error:", err);
            toast.error("خطا در حذف سفارش");
        }
    };

    // مشاهده جزئیات
    const handleViewOrder = (order: Order) => {
        setSelectedOrder(order);
        setShowOrderModal(true);
    };

    // نمایش تصاویر
    const handleShowImages = (images: OrderImage[]) => {
        setSelectedImages(images ?? []);
        setShowImageModal(true);
    };

    // باز کردن fullscreen lightbox روی یک اندیس مشخص
    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // بستن lightbox
    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    // خروجی اکسل
    const handleExportExcel = async () => {
        const token = localStorage.getItem("app_havir");
        if (!token) {
            toast.error("لطفاً مجدداً وارد شوید");
            navigator("/login");
            return;
        }

        toast.info("در حال دریافت اطلاعات، لطفاً صبر کنید...");

        try {
            // مرحله 1: دریافت تعداد کل رکوردها (با page=1 و size=1)
            const totalRes = await axios.get<ApiResponse>(
                "https://rasad-api.havirard.ir/monitoring/orders",
                {
                    params: { page: 1, size: 1 },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const totalRecords = totalRes.data.total;
            if (totalRecords === 0) {
                toast.warning("هیچ سفارشی برای خروجی وجود ندارد");
                return;
            }

            // مرحله 2: دریافت همه رکوردها با size=100
            const pageSize = 100;
            const totalPages = Math.ceil(totalRecords / pageSize);
            let allOrders: Order[] = [];

            for (let page = 1; page <= totalPages; page++) {
                const res = await axios.get<ApiResponse>(
                    "https://rasad-api.havirard.ir/monitoring/orders",
                    {
                        params: { page, size: pageSize },
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                allOrders = [...allOrders, ...res.data.items];
            }

            // ساخت دیتای اکسل
            const exportData = allOrders.map((o) => ({
                "کد سفارش": o.order_code,
                "تاریخ حواله": o.voucher_date_jalali,
                "تاریخ سفارش": o.order_date_jalali,
                "نانوا": o.baker_name,
                "کد ملی نانوا": o.baker_national_id,
                "تلفن نانوا": o.baker_phone,
                "شهرستان": o.city,
                "استان": o.province,
                "راننده": o.driver_name,
                "کد ملی راننده": o.driver_national_id,
                "پلاک خودرو": o.car_plate,
                "نوع خودرو": o.car_type,
                "کیسه حواله": o.voucher_bag_count,
                "کیسه کل": o.total_bag_count,
                "مرحله توزیع": o.distribution_stage,
                "کارخانه": o.flour_factory,
                "شرکت حمل": o.shipping_company,
                "پیمانکار حمل": o.shipping_contractor,
                "تاریخ تحویل": o.delivery_date_jalali,
                "تاریخ تحویل کارخانه": o.factory_delivery_date_jalali,
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "لیست سفارشات");
            XLSX.writeFile(wb, `سفارشات_${new Date().toLocaleDateString('fa-IR')}.xlsx`);

            toast.success(`${allOrders.length} سفارش با موفقیت صادر شد`);
        } catch (err: any) {
            console.error("Export error:", err);
            if (err.response?.status === 401) {
                toast.error("نشست شما منقضی شده است، لطفاً مجدداً وارد شوید");
                navigator("/login");
            } else if (err.response?.status === 422) {
                toast.error("پارامترهای ارسالی معتبر نیست. لطفاً با پشتیبانی تماس بگیرید.");
            } else {
                toast.error("خطا در دریافت یا خروجی اطلاعات");
            }
        }
    };

    // ========== فرم ایجاد/ویرایش ==========
    const formValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            order_id: "",
            order_code: "",
            distribution_period: "",
            distribution_period_code: "",
            distribution_stage: "",
            distribution_stage_code: "",
            flour_factory: "",
            flour_factory_code: "",
            shipping_company: "",
            shipping_contractor: "",
            driver_name: "",
            driver_national_id: "",
            car_plate: "",
            car_type: "",
            province: "",
            province_id: 0,
            city: "",
            city_id: 0,
            baker_name: "",
            baker_code: "",
            baker_national_id: "",
            baker_phone: "",
            voucher_bag_count: 0,
            total_bag_count: 0,
        },
        validationSchema: Yup.object({
            order_code: Yup.string().required("کد سفارش الزامی است"),
            driver_national_id: Yup.string().required("کد ملی راننده الزامی است"),
        }),
        onSubmit: async (values) => {
            const payload = {
                ...values,
                order_date: orderDate ? shamsiToGregorianISO(orderDate) : "",
                voucher_date: voucherDate ? shamsiToGregorianISO(voucherDate) : "",
                factory_delivery_date: factoryDeliveryDate ? shamsiToGregorianISO(factoryDeliveryDate) : "",
                delivery_date: deliveryDate ? shamsiToGregorianISO(deliveryDate) : "",
            };

            try {
                if (isEditMode && editOrderId !== null) {
                    await axios.patch(
                        `https://rasad-api.havirard.ir/monitoring/orders/${editOrderId}/`,
                        payload,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                            },
                        }
                    );
                    toast.success("سفارش با موفقیت ویرایش شد");
                } else {
                    await axios.post(
                        "https://rasad-api.havirard.ir/monitoring/orders",
                        payload,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                            },
                        }
                    );
                    toast.success("سفارش جدید ثبت شد");
                }
                setShowFormModal(false);
                fetchOrders(currentPage, selectedNumber);
            } catch (err: any) {
                console.log(err);
                toast.error(err.response?.data?.message || "خطا در ذخیره سفارش");
            }
        },
    });

    const resetDateStates = (order?: Order) => {
        if (order) {
            setOrderDate(gregorianISOToShamsi(order.order_date));
            setVoucherDate(gregorianISOToShamsi(order.voucher_date));
            setFactoryDeliveryDate(gregorianISOToShamsi(order.factory_delivery_date));
            setDeliveryDate(gregorianISOToShamsi(order.delivery_date));
        } else {
            setOrderDate(new DateObject({ calendar: persian }).setLocale(persian_fa));
            setVoucherDate(null);
            setFactoryDeliveryDate(null);
            setDeliveryDate(null);
        }
    };

    const openCreateModal = () => {
        setIsEditMode(false);
        setEditOrderId(null);
        formValidation.resetForm();
        resetDateStates();
        setShowFormModal(true);
    };

    const openEditModal = (order: Order) => {
        setIsEditMode(true);
        setEditOrderId(order.id);
        formValidation.setValues({
            order_id: order.order_id || "",
            order_code: order.order_code || "",
            distribution_period: order.distribution_period || "",
            distribution_period_code: order.distribution_period_code || "",
            distribution_stage: order.distribution_stage || "",
            distribution_stage_code: order.distribution_stage_code || "",
            flour_factory: order.flour_factory || "",
            flour_factory_code: order.flour_factory_code || "",
            shipping_company: order.shipping_company || "",
            shipping_contractor: order.shipping_contractor || "",
            driver_name: order.driver_name || "",
            driver_national_id: order.driver_national_id || "",
            car_plate: order.car_plate || "",
            car_type: order.car_type || "",
            province: order.province || "",
            province_id: order.province_id || 0,
            city: order.city || "",
            city_id: order.city_id || 0,
            baker_name: order.baker_name || "",
            baker_code: order.baker_code || "",
            baker_national_id: order.baker_national_id || "",
            baker_phone: order.baker_phone || "",
            voucher_bag_count: order.voucher_bag_count ?? 0,
            total_bag_count: order.total_bag_count ?? 0,
        });
        resetDateStates(order);
        setShowFormModal(true);
    };

    // ========== ستون‌های جدول (بدون تغییر) ==========
    const columns = useMemo(() => {
        const baseColumns: any[] = [
            {
                header: "تاریخ حواله",
                accessorKey: "voucher_date_jalali",
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "شهرستان",
                accessorKey: "city",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "نانوا",
                accessorKey: "baker_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            // آدرس نانوا: مرجع آدرس، فیلد baker_address پاسخ همین endpoint است
            // (از پروفایل خبازی خوانده می‌شود و نباید سمت Frontend ساخته شود)
            {
                header: "آدرس نانوا",
                accessorKey: "baker_address",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const address = cell.getValue();
                    if (!address) return <span className="text-slate-400">ثبت نشده</span>;
                    return (
                        <span className="block max-w-[220px] truncate" title={address}>
                            {address}
                        </span>
                    );
                },
            },
            {
                header: "راننده",
                accessorKey: "driver_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "کد ملی راننده",
                accessorKey: "driver_national_id",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "کیسه حواله",
                accessorKey: "voucher_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "کیسه کل",
                accessorKey: "total_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "مرحله توزیع",
                accessorKey: "distribution_stage",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "کارخانه",
                accessorKey: "flour_factory",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "شرکت حمل",
                accessorKey: "shipping_company",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "پیمانکار حمل",
                accessorKey: "shipping_contractor",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
        ];
        baseColumns.push({
            header: "عملیات",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
                const order = cell.row.original as Order;
                const isShippingCompany = userRole === 7; // شرکت حمل

                return (
                    <div className="flex gap-2">
                        {/* دکمه ویرایش – برای همه به جز مدیران استان (۵) و شهرستان (۶) نمایش داده شود (شرکت حمل هم مجاز است) */}
                        {userRole !== 5 && userRole !== 6 && (
                            <button
                                onClick={() => openEditModal(order)}
                                className="p-2 text-amber-500 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                title="ویرایش"
                            >
                                <Pencil size={18} />
                            </button>
                        )}
                        {/* دکمه حذف – فقط برای نقش‌های غیر از مدیران استان (۵)، شهرستان (۶) و شرکت حمل (۷) نمایش داده شود */}
                        {userRole !== 5 && userRole !== 6 && !isShippingCompany && (
                            <button
                                onClick={() => handleDeleteClick(order.id)}
                                className="p-2 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="حذف"
                            >
                                <Trash2 size={18} />
                            </button>
                        )}
                    </div>
                );
            },
        });

        return baseColumns;
    }, [userRole, openEditModal, handleDeleteClick]);
    // ========== رندر ==========
    return (
        <React.Fragment>
            <BreadCrumb pageTitle="لیست سفارشات" title="پایش آرد" />
            <DeleteModal show={deleteModal} onHide={() => setDeleteModal(false)} onDelete={confirmDelete} />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />
            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex gap-2">
                            <TableDropDownSmall onSelect={handleSelect} />
                            {userRole !== 5 && userRole !== 4 && userRole !== 7 ? (
                                <button
                                    onClick={openCreateModal}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700"
                                >
                                    <Plus size={16} /> ایجاد سفارش جدید
                                </button>
                            ) : null}
                        </div>
                        <button
                            onClick={handleExportExcel}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50 dark:bg-gray-800 dark:border-gray-700 dark:text-blue-400 dark:hover:bg-gray-700"
                        >
                            <Download size={16} /> اکسل
                        </button>
                    </div>
                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="جستجو..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            enterKeyHint="search"
                            className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                        />
                    </div>
                    {orders && orders.length > 0 ? (
                        <TableContainer
                            isPagination={false}
                            columns={columns}
                            isGlobalFilter={false}
                            data={orders}
                            customPageSize={selectedNumber}
                            divclassName="-mx-5 overflow-x-auto"
                            tableclassName="w-full whitespace-nowrap"
                            theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                            thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                            tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                            PaginationClassName="hidden"
                        />
                    ) : (
                        <LoadingCounter count={15} text={errorMessage || "داده‌ای در جدول وجود ندارد"} />
                    )}

                    <PaginationTest
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            {/* مودال جزئیات سفارش */}
            <Modal
                show={showOrderModal}
                onHide={() => setShowOrderModal(false)}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">
                        سفارش شماره {selectedOrder?.order_code}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    {selectedOrder && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div><strong>دوره توزیع:</strong> {selectedOrder.distribution_period || "-"}</div>
                            <div><strong>مرحله توزیع:</strong> {selectedOrder.distribution_stage || "-"}</div>
                            <div><strong>تاریخ سفارش:</strong> {selectedOrder.order_date_jalali || "-"}</div>
                            <div><strong>تاریخ حواله:</strong> {selectedOrder.voucher_date_jalali || "-"}</div>
                            <div><strong>کارخانه:</strong> {selectedOrder.flour_factory || "-"}</div>
                            <div><strong>شرکت حمل:</strong> {selectedOrder.shipping_company || "-"}</div>
                            <div>
                                <strong>راننده:</strong> {selectedOrder.driver_name || "-"}
                                {selectedOrder.driver_national_id ? ` (${selectedOrder.driver_national_id})` : ""}
                            </div>
                            <div>
                                <strong>نانوا:</strong> {selectedOrder.baker_name || "-"}
                                {selectedOrder.baker_phone ? ` (${selectedOrder.baker_phone})` : ""}
                            </div>
                            <div><strong>شهر:</strong> {selectedOrder.city || "-"}</div>
                            <div className="md:col-span-2">
                                <strong>آدرس نانوا:</strong> {selectedOrder.baker_address || "ثبت نشده"}
                            </div>
                            <div><strong>کیسه حواله:</strong> {selectedOrder.voucher_bag_count ?? "-"}</div>
                            <div><strong>کیسه کل:</strong> {selectedOrder.total_bag_count ?? "-"}</div>
                            <div><strong>تعداد تخلیه راننده:</strong> {selectedOrder.driver_discharge_count ?? "-"}</div>
                            <div><strong>تعداد بازرس:</strong> {selectedOrder.inspector_counted_count ?? "-"}</div>

                            <div className="md:col-span-2">
                                <strong>مشاهده تصاویر ثبت شده:</strong>{" "}
                                {selectedOrder.images?.length > 0 ? (
                                    <button
                                        onClick={() => handleShowImages(selectedOrder.images)}
                                        className="text-blue-600 underline"
                                    >
                                        {selectedOrder.images.length} عکس
                                    </button>
                                ) : (
                                    "ندارد"
                                )}
                            </div>
                            <div className="md:col-span-2 -mx-5"> {/* کشیدن نقشه تا لبه‌های مودال */}
                                <MapViewer
                                    lat={selectedOrder.driver_location_lat}
                                    lng={selectedOrder.driver_location_lng}
                                />
                            </div>

                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="flex justify-end p-5 border-t dark:border-gray-700">
                    <button onClick={() => setShowOrderModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600">
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>

            {/* مودال گالری تصاویر – استایل شیشه‌ای */}
            <Modal
                show={showImageModal}
                onHide={() => setShowImageModal(false)}
                modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl text-white p-0 overflow-hidden"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <Modal.Title className="text-lg text-white font-bold">
                        گالری تصاویر
                        <span className="block text-sm font-normal text-white/70 mt-1">
                            برای بزرگ‌نمایی روی عکس کلیک کنید
                        </span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {selectedImages.length === 0 ? (
                        <p className="text-center text-white/50 py-10">تصویری وجود ندارد</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            {selectedImages.map((img, idx) => (
                                <div
                                    key={img.id}
                                    className="group relative border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                                    onClick={() => openLightbox(idx)}
                                >
                                    <img
                                        src={img.url}
                                        alt={img.file_name}
                                        className="w-full h-40 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                                        <div className="text-xs text-white/50">
                                            <span className="bg-white/20 text-white border-l backdrop-blur-sm px-5 py-1 rounded-full">
                                                {img.category === "pre_unloading" ? "قبل تخلیه" : "بعد تخلیه"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="flex justify-end p-4 border-t border-white/10">
                    <button
                        onClick={() => setShowImageModal(false)}
                        className="px-5 py-2 text-sm bg-white/10 backdrop-blur-md border border-white/20 rounded-xl hover:bg-white/20 transition-colors text-white"
                    >
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Lightbox تمام‌صفحه با Swiper */}
            {lightboxOpen && (
                <div className="fixed inset-0 text-center z-[9999] bg-black bg-opacity-95 flex items-center justify-center">
                    <button
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
                    >
                        ✕
                    </button>

                    <Swiper
                        modules={[Navigation, Pagination]}
                        navigation={{
                            prevEl: ".custom-prev",
                            nextEl: ".custom-next",
                        }}
                        pagination={{
                            clickable: true,
                            dynamicBullets: true,
                        }}
                        initialSlide={lightboxIndex}
                        spaceBetween={30}
                        slidesPerView={1}
                        className="w-full h-full"
                        style={{ zIndex: 40 }}
                    >
                        {selectedImages.map((img) => (
                            <SwiperSlide key={img.id} className="flex items-center justify-center p-4">
                                <img
                                    src={img.url}
                                    alt={img.file_name}
                                    className="max-h-full max-w-full text-center object-contain select-none"
                                    draggable={false}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div className="custom-prev absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl cursor-pointer hover:text-gray-300 z-50">
                        <ChevronLeft size={48} />
                    </div>
                    <div className="custom-next absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl cursor-pointer hover:text-gray-300 z-50">
                        <ChevronRight size={48} />
                    </div>
                </div>
            )}

            {/* مودال فرم ایجاد/ویرایش (با دراپ‌داون استان و شهر) */}
            <Modal
                show={showFormModal}
                onHide={() => setShowFormModal(false)}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[65rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">
                        {isEditMode ? "ویرایش " : "ایجاد سفارش جدید"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    <form id="order-form" onSubmit={formValidation.handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد سفارش *</label>
                                <input name="order_code" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.order_code} />
                                {formValidation.touched.order_code && formValidation.errors.order_code && <p className="text-red-500 text-xs">{formValidation.errors.order_code}</p>}
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">شناسه سفارش (order_id)</label>
                                <input name="order_id" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.order_id} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد ملی راننده *</label>
                                <input name="driver_national_id" type="text" className="w-full  px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.driver_national_id} />
                                {formValidation.touched.driver_national_id && formValidation.errors.driver_national_id && <p className="text-red-500 text-xs">{formValidation.errors.driver_national_id}</p>}
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">نام راننده</label>
                                <input name="driver_name" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.driver_name} />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium">تاریخ سفارش</label>
                                <DatePicker
                                    calendar={persian}
                                    locale={persian_fa}
                                    value={orderDate}
                                    onChange={setOrderDate}
                                    format="YYYY/MM/DD"
                                    calendarPosition="bottom-right"
                                    containerClassName="w-full"
                                    inputClass="form-input w-full p-2 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">تاریخ حواله</label>
                                <DatePicker
                                    calendar={persian}
                                    locale={persian_fa}
                                    value={voucherDate}
                                    onChange={setVoucherDate}
                                    format="YYYY/MM/DD"
                                    calendarPosition="bottom-right"
                                    containerClassName="w-full"
                                    inputClass="form-input w-full p-2 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">تاریخ تحویل کارخانه</label>
                                <DatePicker
                                    calendar={persian}
                                    locale={persian_fa}
                                    value={factoryDeliveryDate}
                                    onChange={setFactoryDeliveryDate}
                                    format="YYYY/MM/DD"
                                    calendarPosition="bottom-right"
                                    containerClassName="w-full"
                                    inputClass="form-input w-full p-2 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">تاریخ تحویل به نانوا</label>
                                <DatePicker
                                    calendar={persian}
                                    locale={persian_fa}
                                    value={deliveryDate}
                                    onChange={setDeliveryDate}
                                    format="YYYY/MM/DD"
                                    calendarPosition="bottom-right"
                                    containerClassName="w-full"
                                    inputClass="form-input w-full p-2 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium">استان</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                                    value={formValidation.values.province}
                                    onChange={(e) => {
                                        const selectedProvince = e.target.value;
                                        const prov = provincesList.find(p => p.province === selectedProvince);
                                        formValidation.setFieldValue("province", selectedProvince);
                                        formValidation.setFieldValue("province_id", prov ? prov.province_id : 0);
                                    }}
                                >
                                    <option value="">انتخاب استان</option>
                                    {provincesList.map((p) => (
                                        <option key={p.province_id} value={p.province}>{p.province}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">شهر</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                                    value={formValidation.values.city}
                                    onChange={(e) => {
                                        const selectedCity = e.target.value;
                                        const cty = citiesList.find(c => c.city === selectedCity);
                                        formValidation.setFieldValue("city", selectedCity);
                                        formValidation.setFieldValue("city_id", cty ? cty.city_id : 0);
                                    }}
                                >
                                    <option value="">انتخاب شهر</option>
                                    {citiesList.map((c) => (
                                        <option key={c.city_id} value={c.city}>{c.city}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium">دوره توزیع</label>
                                <input name="distribution_period" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.distribution_period} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد دوره توزیع</label>
                                <input name="distribution_period_code" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.distribution_period_code} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">مرحله توزیع</label>
                                <input name="distribution_stage" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.distribution_stage} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد مرحله توزیع</label>
                                <input name="distribution_stage_code" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.distribution_stage_code} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کارخانه</label>
                                <input name="flour_factory" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.flour_factory} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد کارخانه</label>
                                <input name="flour_factory_code" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.flour_factory_code} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">شرکت حمل</label>
                                <input name="shipping_company" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.shipping_company} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">پیمانکار حمل</label>
                                <input name="shipping_contractor" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.shipping_contractor} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">پلاک خودرو</label>
                                <input name="car_plate" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.car_plate} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">نوع خودرو</label>
                                <input name="car_type" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.car_type} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">نام نانوا</label>
                                <input name="baker_name" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.baker_name} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد نانوا</label>
                                <input name="baker_code" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.baker_code} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کد ملی نانوا</label>
                                <input name="baker_national_id" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.baker_national_id} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">تلفن نانوا</label>
                                <input name="baker_phone" type="text" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.baker_phone} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کیسه حواله</label>
                                <input name="voucher_bag_count" type="number" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.voucher_bag_count} />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium">کیسه کل</label>
                                <input name="total_bag_count" type="number" className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" onChange={formValidation.handleChange} value={formValidation.values.total_bag_count} />
                            </div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3 p-5 border-t dark:border-gray-700">
                    <button onClick={() => setShowFormModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600">انصراف</button>
                    <button type="submit" form="order-form" className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        {isEditMode ? "ذخیره تغییرات" : "ایجاد سفارش"}
                    </button>
                </Modal.Footer>
            </Modal>


        </React.Fragment>
    );
};

export default ManageOrders;